import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { finalize, Subject, Subscription, takeUntil } from 'rxjs';
import * as _ from 'lodash-es';
import { GtwSnackbarService } from '../../../../shared/_services/gtw-snackbar.service';
import { GlobalService } from '../../../../global/services/global.service';
import { WorkspaceService } from '../../../../workspace/workspace.service';
import { AppService } from '../../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-trueup905c-comments',
    templateUrl: './trueup905ccomments.component.html',
    styleUrls: ['./trueup905ccomments.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class Trueup905cCommentsComponent implements OnInit, OnDestroy {
  private static readonly bridgeHostSelector = 'gtw-trueup905c-comments-ce';

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private unsubscribe$ = new Subject<void>();
  private loadSeq = 0;
  /** Bumped on row change / reinit so late HTTP responses do not mutate UI or show toasts. */
  private staleEpoch = 0;
  private readonly contextSubs = new Subscription();
  private contextEpoch = 0;
  private isLoadingComments = false;
  
  // Component state
  modalTitle = 'Comments';
  minimise = false;
  isInitialized = false;
  loading = false;
  
  // Data properties
  COMMENTS = '';
  OLD_COMMENTS = '';
  allComments: any[] = [];
  
  // Global configuration
  GENERAL_CONFIG: any = {};
  baseURLs: any = {};
  globalService: any = {};
  filterParams: any = {};
  rowData: any = {};
  logged_in_user = '';

  constructor(
    private http: HttpClient,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.resetAndTryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.resetAndTryInit();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.resetAndTryInit();
      })
    );
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.resetInitIfRowIdentityChanged();
    this.logged_in_user = this.globalService?.userSettingsData?.user?.sso_id || (this.globalService as any)?.sso_id || '';
  }

  private resetAndTryInit(): void {
    this.contextEpoch++;
    this.isInitialized = false;
    this.applyWorkspaceContext();
    this.tryInit();
    this.cdr.markForCheck();
  }

  private resolveApiBase(): string {
    const u = this.baseURLs || {};
    const g = this.GENERAL_CONFIG || {};
    return String(u.api ?? g.api ?? g.base_url ?? '').trim();
  }

  private lastRowInitKey = '';

  private resetInitIfRowIdentityChanged(): void {
    const key = `${this.rowData?.COMBINATION_KEY ?? ''}|${this.rowData?.GROUP_TRANS_KEY ?? ''}|${this.rowData?.TAX_YEAR ?? ''}`;
    if (this.lastRowInitKey && key !== this.lastRowInitKey) {
      this.isInitialized = false;
      this.isLoadingComments = false;
      this.loadSeq++;
      this.staleEpoch++;
    }
    this.lastRowInitKey = key;
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoadingComments) {
      return;
    }

    const api = this.resolveApiBase();
    if (!api || !this.rowData?.COMBINATION_KEY) {
      return;
    }

    const gp = this.globalService?.globalParams;
    if (!gp || !Object.keys(gp).length) {
      return;
    }

    const taxYear = this.filterParams?.tax_year ?? this.globalService?.globalParams?.tax_year;
    if (taxYear === undefined || taxYear === null || taxYear === '') {
      return;
    }

    this.isInitialized = true;
    this.isLoadingComments = true;
    const seq = ++this.loadSeq;
    this.loadAllComments(seq);
  }

  loadAllComments(responseSeq: number) {
    // Specific params for this API call (matching AngularJS getAllComments)
    const specificParams: any = {
      action_code: 'xcko1g',
      tax_year: this.rowData.TAX_YEAR,
      scenario: this.filterParams?.scenario || this.globalService?.globalParams?.scenario,
      jcd_key: this.globalService?.globalParams?.jcd_key || 250,
      combination_key: this.rowData.COMBINATION_KEY,
      currency: this.rowData.PARENT_FUNCTIONAL_CURRENCY,
      sso_id: this.logged_in_user,
      GROUP_TRANS_KEY: this.rowData.GROUP_TRANS_KEY
    };
    
    // Merge params like AngularJS JsonObjectFactory.getJSONObj does:
    // params = _.merge({}, GlobalService.globalParams, filterParams, specificParams)
    // This ensures filing_key, filter_by, parent_me_string are included
    let params = _.merge(
      {},
      this.globalService?.globalParams || {},
      this.filterParams || {},
      specificParams
    );
    
    // Remove params that AngularJS doesn't send for this API
    delete params.checkLockedbj;
    delete params.scenarioCode;
    delete params.is_issue_key_enabled;
    
    const api = this.resolveApiBase();
    this.http.get<any>(api + '/loadJsonObject', { 
      params: params,
      withCredentials: true 
    }).pipe(
      takeUntil(this.unsubscribe$),
      finalize(() => {
        this.isLoadingComments = false;
      })
    )
    .subscribe((data: any) => {
      if (responseSeq !== this.loadSeq) {
        return;
      }
      this.allComments = data?.jsonObject || [];
    }, () => {
      if (responseSeq === this.loadSeq) {
        this.isInitialized = false;
      }
    });
  }

  saveComments() {
    // Validation
    if (this.COMMENTS === undefined || this.COMMENTS === null || this.COMMENTS === '') {
      this.gtwSnackbarService.openSnackBar('Please enter a valid comments', 'gtw-snackbar--red');
      return;
    }

    if (this.COMMENTS.length > 2000) {
      this.gtwSnackbarService.openSnackBar('Please comments entered cannot be more than 2000.', 'gtw-snackbar--red');
      return;
    }

    if (this.loading) {
      return;
    }
    this.loading = true;
    const saveEpoch = this.staleEpoch;

    const api = this.resolveApiBase();
    if (!api) {
      this.loading = false;
      this.gtwSnackbarService.openSnackBar('Unable to resolve API base', 'gtw-snackbar--red');
      return;
    }

    const url = api + '/saveJsonObject?action_code=3pu788';

    const jsonObj = [{
      combination_key: this.rowData.COMBINATION_KEY,
      chart_key: this.rowData.CHART_KEY,
      currency: this.rowData.PARENT_FUNCTIONAL_CURRENCY,
      comments: this.COMMENTS.replace(/'/g, "'"),
      GROUP_TRANS_KEY: this.rowData.GROUP_TRANS_KEY
    }];

    const jsonSettings = [{
      combination_key: this.rowData.COMBINATION_KEY,
      tax_year: this.rowData.TAX_YEAR
    }];

    // Build params matching AngularJS pattern exactly:
    let params: any = _.merge({}, this.globalService?.globalParams || {}, this.filterParams);
    params = _.extend({ jsonObj: JSON.stringify(jsonObj) }, params);
    params = _.extend({ jsonSetting: JSON.stringify(jsonSettings) }, params);
    params.process_name = 'TaxTrueUpCommentsSave';
    
    // NOTE: AngularJS saveComments does NOT add checkLockedbj - only the merge above

    // Use URLSearchParams with application/x-www-form-urlencoded
    const urlEncoded = new URLSearchParams();
    Object.keys(params).forEach(key => {
      if (params[key] !== undefined && params[key] !== null) {
        urlEncoded.set(key, params[key].toString());
      }
    });

    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    this.http.post<any>(url, urlEncoded.toString(), { headers, withCredentials: true })
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          this.loading = false;
        })
      )
      .subscribe((result: any) => {
        if (saveEpoch !== this.staleEpoch) {
          return;
        }
        if (result.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar('Data saved Successfully.', 'gtw-snackbar--green');
          this.notifyGridUpdate('refresh');
        } else {
          if (result.errorMessage) {
            this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
          } else {
            this.gtwSnackbarService.openSnackBar(
              'An error has occurred. If this error continues please go to the help page and contact support',
              'gtw-snackbar--red'
            );
          }
        }
      }, () => {
        if (saveEpoch !== this.staleEpoch) {
          return;
        }
        this.gtwSnackbarService.openSnackBar('An error occurred while saving', 'gtw-snackbar--red');
      });
  }

  private notifyGridUpdate(detail: 'refresh' | 'cancel'): void {
    this.requestGridUpdate.emit(detail);
    const host = document.querySelector(Trueup905cCommentsComponent.bridgeHostSelector);
    host?.dispatchEvent(new CustomEvent('request-grid-update', { detail, bubbles: true }));
  }

  cancel() {
    this.notifyGridUpdate('cancel');
  }

  ngOnDestroy(): void {
    this.contextEpoch++;
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  reinitialize() {
    this.isInitialized = false;
    this.isLoadingComments = false;
    this.loadSeq++;
    this.staleEpoch++;
    this.tryInit();
  }
}

